import { useCallback, useMemo } from 'react';
import moment from 'moment';
import { useMutation } from '@tanstack/react-query';
import { useSelector } from '@s20/hooks/redux.hooks';
import { getCrewLeadLabel } from '@s20/selectors/features/common.selectors';
import { httpClient } from '@s20/services/httpClient';
import { ApiRoutes } from '../../../../../Common';
import { Modal } from '../../../../../common/Modal';
import {
    getCorrectTime, ConfirmationStatus, getJobTimeZone,
    JobAssignmentCancellationTypes,
} from '../../../../../dispatch/Dispatch';
import { toasty } from '../../../../../common/forms/FormElements';
import { useUpdateAssignment } from '../useUpdateAssignment.hook';
import { CancelJobAssignmentBody } from './CancelJobAssignmentBody';

export const useCancelJobAssignment = (options) => {
    const crewLead = useSelector(getCrewLeadLabel);

    const {
        day, job, jobAssignmentCancellationTypes, onClose, jobAssignmentId,
    } = options || {};

    const dayAssignments = job?.assignments?.[day];
    const jobAssignment = useMemo(() =>
        dayAssignments && [...dayAssignments.staff, ...dayAssignments.equipment].find((asn) => asn.jobAssignmentId === jobAssignmentId),
        [dayAssignments, jobAssignmentId],
    );

    const mutationHandlers = useUpdateAssignment({
        jobAssignmentId,
        jobId: jobAssignment?.jobId,
        mutateOnSuccess: true,
        mutateLinkedEquipment: true,
        onMutate: (employee, isMatch) => {
            if (isMatch) {
                return {
                    ...employee,
                    jobAssignmentId: null,
                    assignment: null,
                };
            }

            return employee;
        },
    });

    const handleCancellation = useMutation({
        ...mutationHandlers,
        mutationFn: async ({
            selectedJobAssignmentCancellationType,
            selectedJobAssignmentCancellationNotes,
            selectedJobAssignmentCancellationTime,
        }) => {
            const selectedJobAssignmentToRemove = { day, job };

            const jobAssignmentCancelTime = moment.tz(
                selectedJobAssignmentCancellationTime,
                getJobTimeZone(selectedJobAssignmentToRemove),
            ).format();


            const model = {
                assignmentId: jobAssignmentId,
                cancellationTypeId: selectedJobAssignmentCancellationType,
                cancellationNote: selectedJobAssignmentCancellationNotes,
                cancellationTime: jobAssignmentCancelTime,
            };

            if (parseInt(selectedJobAssignmentCancellationType ?? 0) <= 0) {
                toasty.error('Please select the cancellation reason.');
                return false;
            }

            const response = await httpClient.post(
                ApiRoutes.dispatch.cancelAssignment(),
                model,
            );

            if (response === 'ok') {
                toasty.success('Notification sent for cancellation');
            } else {
                toasty.error('Notification Error');
            }
        },
    });

    const handleRemoval = useMutation({
        ...mutationHandlers,
        mutationFn: () => httpClient.delete(
            ApiRoutes.dispatch.removeAssignment(jobAssignmentId),
        ),
    });

    const handleCancelJobAssignment = useCallback(() => {
        const selectedDayJobToRemove = { day, job };

        Modal.show({
            onClose,
            title: 'Cancel Job Assignment',
            initialValues: {
                selectedJobAssignmentCancellationTime: getCorrectTime(selectedDayJobToRemove, new Date()),
                selectedJobAssignmentCancellationType: JobAssignmentCancellationTypes.ClientCancel,
            },
            SaveButtonProps: {
                color: 'danger',
                label: 'Cancel Job Assignment',
                savingLabel: 'Cancelling...',
            },
            onSubmit: handleCancellation.mutateAsync,
            body: () => (
                <CancelJobAssignmentBody
                    job={job}
                    day={day}
                    jobAssignmentCancellationTypes={jobAssignmentCancellationTypes}
                    assignment={jobAssignment}
                />
            ),
        });
    }, [onClose, job, day, jobAssignmentCancellationTypes, jobAssignment, handleCancellation.mutateAsync]);

    const handleRemoveDayJob = useCallback(() => {
        Modal.show({
            onClose,
            onSubmit: async () => {
                // this hits too early, and is covered by confirmation/send notifications
                // If Staff Assignment - Need to make sure the scheduler is not removing the crew lead
                if (jobAssignment.assignableGroups) {
                    const staffCount = dayAssignments.staff.filter((s) => !!s.assignment).length;

                    if (jobAssignment.isCrewLead && staffCount > 1) {
                        toasty.error(
                            `Please promote another crew member to the ${crewLead} before removing/cancelling this crew member.  Each job must have a ${crewLead.toLowerCase()}.`,
                        );

                        return false;
                    }
                }

                // show modal and get reason if its not in a status of scheduled.
                if (jobAssignment.assignment.confirmationStatusId >= ConfirmationStatus.NoResponse) {
                    // needs cancellation
                    handleCancelJobAssignment();

                    return { keepOpen: true };
                } else {
                    await handleRemoval.mutateAsync();
                }
            },
            title: 'Cancel Assignment',
            body: () => 'Are you sure you want to cancel this job assignment?',
            SaveButtonProps: {
                label: 'Yes, cancel it',
                savingLabel: 'Cancelling',
                color: 'danger',
            },
        });
    }, [handleCancelJobAssignment, onClose, dayAssignments, crewLead, jobAssignment, handleRemoval.mutateAsync]); // eslint-disable-line

    return handleRemoveDayJob;
};